import { AddAPhoto, CalendarToday, Delete, Email, Person, Save } from '@mui/icons-material';
import { Alert, Avatar, Box, Button, Card, CardContent, CircularProgress, Divider, TextField, Typography, useTheme } from '@mui/material';
import { ChangeEvent, Fragment, JSX, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { parseApiError } from '../../../library/apiErrorHelper';
import { careRoles } from '../../../library/constants';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import { absoluteUserPictureUrl, IUserPicture, personInitial } from '../../../library/models/userPicture';
import { useApiFormError } from '../../../library/useApiFormError';
import { createProfileSchema } from './profileSchema';
import { PushNotificationsSection } from './PushNotificationsSection';
import ResourcesIds from '../../../resources/ResourcesIds';
import { apiService } from '../../../services/api';
import { useReactMutation } from '../../../services/queryService';
import { services } from '../../../services/services';
import { UserUpdateRequest } from '../../../types';
import { LogoCropDialog } from '../../imageCrop/LogoCropDialog';
import { useSnackbar } from '../../layout/snackbarProvider';
import { accountFieldsStyles, accountHeadingStyles, accountLabelStyles, accountSummaryStyles, formGridStyles, leftCardContentStyles, memberSinceIconStyles, pageHeaderContainerStyles, pageHeaderSubtitleStyles, pageHeaderTitleStyles, pageLayoutContainerStyles, pictureActionsStyles, pictureAvatarStyles, pictureBusyStyles, pictureDetailStyles, pictureFrameStyles, pictureSectionDividerStyles, pictureSectionStyles, sectionHeaderContainerStyles, sectionHeaderIconStyles, sectionHeaderTitleStyles, strongBodyTextStyles, submitButtonContainerStyles, usernameLabelIconStyles } from './profile.styles';

export const Profile = (): JSX.Element => {
    const theme = useTheme();
    const { user, role, updateUser } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [success, setSuccess] = useState<string | null>(null);
    const { t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();

    /** The picked file, held only until the crop dialog produces the one that is uploaded. */
    const [pendingPicture, setPendingPicture] = useState<File | null>(null);
    const [isOpeningPicture, setIsOpeningPicture] = useState(false);
    const pictureInput = useRef<HTMLInputElement | null>(null);

    const uploadPicture = useReactMutation(services.users.uploadPictureMutation());
    const removePicture = useReactMutation(services.users.removePictureMutation());

    const pictureSrc: string | null = absoluteUserPictureUrl(user?.pictureUrl);
    const isPictureBusy: boolean =
        uploadPicture.isPending || removePicture.isPending || isOpeningPicture;

    const fullName: string = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

    const schema = useMemo(() => createProfileSchema(translate), [translate]);

    const {
        register, handleSubmit, setError, reset, formState: { errors, isDirty, isValid },
    } = useValidatedForm<UserUpdateRequest>(schema, {
        defaultValues: {
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            phoneNumber: user?.phoneNumber || '',
        },
    });

    const knownFields: string[] = useMemo(() => Object.keys(schema.fields), [schema]);
    // Used to show the server's raw English and drop any per-field detail on the floor.
    const { formError: error, report, clear } = useApiFormError<UserUpdateRequest>(
        setError, knownFields, translate, ResourcesIds.PROFILE_UPDATE_ERROR_DEFAULT);

    useEffect(() => {
        if (user) {
            reset({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                // '' rather than undefined so clearing the field reaches the API as a
                // blank, which is what takes the number off file.
                phoneNumber: user.phoneNumber || '',
            });
        }
    }, [user, reset]);

    /**
     * Picking a file does not upload it. The crop is the subject of the upload, so somebody
     * who picked the wrong photograph finds out in the dialog rather than in the header --
     * the same two-step the branding card uses.
     */
    const onPickPicture = (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        // Cleared straight away so choosing the same file twice still fires a change event.
        event.target.value = '';

        if (file) {
            setPendingPicture(file);
        }
    };

    /**
     * Closes on success only, so a rejected upload leaves the crop as it was for a retry.
     *
     * `updateUser` is what makes the change visible at once: the header avatar is on screen
     * right now, and `AuthContext` would otherwise only pick the new URL up on the next
     * `GET /users/me` -- the next window focus.
     */
    const onCroppedPicture = (cropped: File): void => {
        uploadPicture.mutate(cropped, {
            onSuccess: (stored: IUserPicture) => {
                setPendingPicture(null);
                updateUser({ pictureUrl: stored.pictureUrl });
                showSnackbar(translate(ResourcesIds.PROFILE_PICTURE_SUCCESS), 'success');
            },
            onError: (apiError: Error) =>
                showSnackbar(
                    parseApiError(apiError).message
                        ?? translate(ResourcesIds.PROFILE_PICTURE_ERROR),
                    'error'
                ),
        });
    };

    const onRemovePicture = (): void => {
        removePicture.mutate(undefined, {
            onSuccess: () => {
                updateUser({ pictureUrl: null });
                showSnackbar(translate(ResourcesIds.PROFILE_PICTURE_REMOVED), 'success');
            },
            onError: () => showSnackbar(translate(ResourcesIds.PROFILE_PICTURE_ERROR), 'error'),
        });
    };

    /**
     * Reopens the crop dialog on the picture already stored.
     *
     * What is stored is the *cropped* square, not the photograph originally picked -- that
     * one is gone. So this can tighten a crop but never widen it, which is what "Change
     * picture" is for. Cropping further is pixel-exact rather than lossy, because the output
     * never upscales.
     */
    const onEditPicture = async (): Promise<void> => {
        if (!user?.pictureUrl) {
            return;
        }

        setIsOpeningPicture(true);

        try {
            setPendingPicture(await services.users.fetchPictureFile(user.pictureUrl));
        } catch {
            showSnackbar(translate(ResourcesIds.PROFILE_PICTURE_EDIT_ERROR), 'error');
        } finally {
            setIsOpeningPicture(false);
        }
    };

    const onSubmit = async (data: UserUpdateRequest) => {
        try {
            setIsSubmitting(true);
            clear();
            setSuccess(null);

            if (user?.id) {
                const updatedUser = await apiService.updateUser(user.id, data);
                updateUser(updatedUser);
                setSuccess(translate(ResourcesIds.PROFILE_UPDATE_SUCCESS));
            }
        } catch (err: unknown) {
            report(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    return (
        <>
            <Box sx={pageHeaderContainerStyles}>
                <Typography variant="h4" component="h1" gutterBottom sx={pageHeaderTitleStyles}>
                    {translate(ResourcesIds.PROFILE_SETTINGS_TITLE)}
                </Typography>
                <Typography variant="h6" sx={pageHeaderSubtitleStyles}>
                    {translate(ResourcesIds.PROFILE_SETTINGS_SUBTITLE)}
                </Typography>
            </Box>

            <Box sx={pageLayoutContainerStyles}>
                {/* Everything about the account that can be changed. */}
                <Box>
                    <Card>
                        <CardContent sx={leftCardContentStyles}>
                            {/* Who the page is about, before anything it says about them. */}
                            <Box sx={accountSummaryStyles}>
                                <Typography variant="h6" sx={accountHeadingStyles}>
                                    {fullName || user?.emailAddress}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {user?.emailAddress}
                                </Typography>
                            </Box>

                            <Box sx={sectionHeaderContainerStyles}>
                                <AddAPhoto sx={sectionHeaderIconStyles(theme)} />
                                <Typography variant="h6" sx={sectionHeaderTitleStyles}>
                                    {translate(ResourcesIds.PROFILE_PICTURE_TITLE)}
                                </Typography>
                            </Box>

                            <Box sx={pictureSectionStyles}>
                                {/* Not `UserAvatar`: this one is 88px, carries an upload
                                    control beside it and a spinner over it, and is the only
                                    avatar in the app that is editable rather than drawn. */}
                                <Box sx={pictureFrameStyles}>
                                    <Avatar
                                        src={pictureSrc ?? undefined}
                                        alt={pictureSrc
                                            ? translate(ResourcesIds.PROFILE_PICTURE_ALT,
                                                { name: fullName })
                                            : ''}
                                        sx={pictureAvatarStyles(theme)}
                                    >
                                        {personInitial(user)}
                                    </Avatar>

                                    {isPictureBusy && (
                                        <Box sx={pictureBusyStyles}>
                                            <CircularProgress size={28} />
                                        </Box>
                                    )}
                                </Box>

                                <Box sx={pictureDetailStyles}>
                                    {/* Permissive on purpose, even though the API takes only
                                        photographs: the canvas re-encode is what guarantees
                                        the format, rather than a 400 arguing about it after
                                        the fact. */}
                                    <Box
                                        component="input"
                                        ref={pictureInput}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        data-testid="picture-file-input"
                                        hidden
                                        onChange={onPickPicture}
                                    />

                                    <Box sx={pictureActionsStyles}>
                                        <Button
                                            size="small"
                                            startIcon={<AddAPhoto />}
                                            disabled={isPictureBusy}
                                            onClick={() => pictureInput.current?.click()}
                                        >
                                            {translate(user?.pictureUrl
                                                ? ResourcesIds.PROFILE_PICTURE_CHANGE
                                                : ResourcesIds.PROFILE_PICTURE_UPLOAD)}
                                        </Button>

                                        {user?.pictureUrl && (
                                            <Button
                                                size="small"
                                                color="error"
                                                startIcon={<Delete />}
                                                disabled={isPictureBusy}
                                                onClick={onRemovePicture}
                                            >
                                                {translate(ResourcesIds.PROFILE_PICTURE_REMOVE)}
                                            </Button>
                                        )}
                                    </Box>
                                </Box>

                                <Box sx={accountFieldsStyles}>
                                    {/* Fragments rather than wrappers, so each label and its
                                        value are two direct children of the grid and every
                                        value starts on the same column. See the style. */}
                                    <Fragment>
                                        <Box sx={accountLabelStyles}>
                                            <Email sx={usernameLabelIconStyles(theme)} />
                                            <Typography variant="body2" color="text.secondary">
                                                {translate(ResourcesIds.USERNAME_LABEL)}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={strongBodyTextStyles}>
                                            {user?.emailAddress}
                                        </Typography>
                                    </Fragment>

                                    <Fragment>
                                        <Box sx={accountLabelStyles}>
                                            <CalendarToday sx={memberSinceIconStyles(theme)} />
                                            <Typography variant="body2" color="text.secondary">
                                                {translate(ResourcesIds.MEMBER_SINCE)}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={strongBodyTextStyles}>
                                            {user?.createdAt ? formatDate(user.createdAt) : 'N/A'}
                                        </Typography>
                                    </Fragment>
                                </Box>
                            </Box>

                            {/* For whoever can be in an appointment, which is who there is ever
                                anything to notify. Null reads as CUSTOMER, as in the header. */}
                            {careRoles.includes(role ?? 'CUSTOMER') && <PushNotificationsSection />}

                            <Divider sx={pictureSectionDividerStyles} />

                            <Box sx={sectionHeaderContainerStyles}>
                                <Person sx={sectionHeaderIconStyles(theme)} />
                                <Typography variant="h6" sx={sectionHeaderTitleStyles}>
                                    {translate(ResourcesIds.PERSONAL_INFORMATION)}
                                </Typography>
                            </Box>

                            {error && (
                                <Alert severity="error" sx={{ mb: 3 }}>
                                    {error}
                                </Alert>
                            )}

                            {success && (
                                <Alert severity="success" sx={{ mb: 3 }}>
                                    {success}
                                </Alert>
                            )}

                            <form onSubmit={handleSubmit(onSubmit)}>
                                <Box sx={formGridStyles}>
                                    <TextField
                                        {...register('firstName')}
                                        fullWidth
                                        label={translate(ResourcesIds.FIRST_NAME)}
                                        {...fieldProps(errors, 'firstName')}
                                    />
                                    <TextField
                                        {...register('lastName')}
                                        fullWidth
                                        label={translate(ResourcesIds.LAST_NAME)}
                                        {...fieldProps(errors, 'lastName')}
                                    />
                                    <TextField
                                        {...register('phoneNumber')}
                                        fullWidth
                                        type="tel"
                                        label={translate(ResourcesIds.PHONE_LABEL)}
                                        {...fieldProps(errors, 'phoneNumber')}
                                    />
                                </Box>

                                <Box sx={submitButtonContainerStyles}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={isSubmitting || !isDirty || !isValid}
                                        startIcon={<Save />}
                                        sx={{ minWidth: 150 }}
                                    >
                                        {isSubmitting ? translate(ResourcesIds.SAVING) : translate(ResourcesIds.SAVE_CHANGES)}
                                    </Button>
                                </Box>
                            </form>
                        </CardContent>
                    </Card>
                </Box>

            </Box>

            <LogoCropDialog
                file={pendingPicture}
                variant="avatar"
                isUploading={uploadPicture.isPending}
                onCancel={() => setPendingPicture(null)}
                onConfirm={onCroppedPicture}
            />
        </>
    );
};